import { Actions, FSpread } from '@client/new/helpers/types';
import { useVideoStateModalStore } from '@client/new/stores/videoStateModal.store';
import { useState } from 'react';
import _ from 'lodash';
import { NotificationsVM } from '@client/new/helpers/useNotifcations';
import {
  UploadVideoWriteModel,
  useUploadVideoWriteModel,
} from '../models/uploadVideo.model';
import { faWindowMinimize } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { floatingWindowText, getListStatus, mappedLists } from '../helpers';

type PayloadMap = {
  closeModal: never;
  confirmCancel: never;
  cancel: never;
  upload: never;
  toggle: never;
  onDrop: File[];
  closeConfirmation: never;
  deleteFile: string;
  editFile: number;
  saveFileUpdate: { index: number; name?: string; timestamp?: number };
  cancelSingleUpload: string;
  reupload: never;
  cancelFileEdit: never;
};

type UploadVMAction = Actions<PayloadMap>;
export type UploadVMDispatcher = FSpread<PayloadMap>;

export type UploadVideoVM = {
  dispatch: UploadVMDispatcher;
  modal: {
    showDropZone: boolean;
    uploadButton: {
      show: boolean;
      disabled: boolean;
    };
    showMinimizeModal: boolean;
    open: boolean;
    customCloseIcon?: IconProp;
  };
  confirmationModal: { open: boolean };
  floatingWindow: {
    title: string;
    open: boolean;
    detailsLeft?: string;
    detailsRight?: string;
  };
  list: {
    show: boolean;
    listStatus: {
      primary: string;
      secondary?: string;
    };
    showItemEdit: boolean;
    failedFiles: UploadVideoWriteModel['files'];
    invalidFiles: UploadVideoWriteModel['files'];
  } & Pick<UploadVideoWriteModel, 'uploadMap' | 'fileInEdit' | 'files'>;
  notifications: NotificationsVM;
};

export const useUploadVideoVM = (): UploadVideoVM => {
  const { mode, close, minimize, maximize, influencerId, folderId, onSuccess } =
    useVideoStateModalStore();
  const writeModel = useUploadVideoWriteModel({ influencerId });
  const [confirmClose, setConfirmClose] = useState(false);

  const dispatch: UploadVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as UploadVMAction;
    switch (action.type) {
      case 'cancel':
        if (writeModel.files.length === 0 || writeModel.uploadFinished) {
          cancel();
          return null;
        }
        setConfirmClose(true);
        return null;
      case 'closeModal':
        if (writeModel.isUploading) {
          minimize();
          return null;
        }
        if (writeModel.files.length === 0 || writeModel.uploadFinished) {
          cancel();
          return null;
        }
        setConfirmClose(true);
        return null;
      case 'confirmCancel':
        cancel();
        return null;
      case 'upload': {
        writeModel.dispatch('upload', { folderId, onSuccess });
        return null;
      }
      case 'toggle':
        maximize();
        return null;
      case 'onDrop': {
        writeModel.dispatch('onDrop', action.payload);
        return null;
      }
      case 'closeConfirmation':
        setConfirmClose(false);
        return null;
      case 'deleteFile':
        writeModel.dispatch('deleteFile', action.payload);
        return null;
      case 'editFile':
        writeModel.dispatch('editFile', action.payload);
        return null;
      case 'cancelFileEdit':
        writeModel.dispatch('cancelFileEdit');
        return null;
      case 'saveFileUpdate':
        writeModel.dispatch('saveFileUpdate', action.payload);
        return null;
      case 'cancelSingleUpload':
        writeModel.dispatch('cancelSingleUpload', action.payload);
        return null;
      case 'reupload':
        writeModel.dispatch('reupload');
        return null;
    }
  };

  const cancel = () => {
    writeModel.dispatch('cancel');
    setConfirmClose(false);
    close();
  };

  return {
    dispatch,
    floatingWindow: {
      open: mode === 'minimized',
      ...floatingWindowText(writeModel.uploadMap),
    },
    modal: {
      showDropZone:
        !writeModel.isUploading &&
        writeModel.fileInEdit === null &&
        !writeModel.uploadFinished,
      uploadButton: {
        show: !writeModel.isUploading && !writeModel.uploadFinished,
        disabled: !writeModel.canUpload || writeModel.fileInEdit !== null,
      },
      showMinimizeModal: writeModel.isUploading,
      open: mode === 'open',
      customCloseIcon: writeModel.isUploading ? faWindowMinimize : undefined,
    },
    confirmationModal: {
      open: confirmClose,
    },
    list: {
      show: !_.isEmpty(writeModel.files),
      ...mappedLists(writeModel.files, writeModel.uploadMap),
      fileInEdit: writeModel.fileInEdit,
      listStatus: getListStatus(
        writeModel.isUploading,
        writeModel.files,
        writeModel.uploadMap,
      ),
      showItemEdit: writeModel.fileInEdit !== null && !writeModel.isUploading,
      uploadMap: writeModel.uploadMap,
    },
    notifications: writeModel.notifications,
  };
};
